import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Database, Loader2, HardDrive, CheckCircle, Settings,
  Save, Server, Wifi, WifiOff, FolderOpen, Globe,
  RotateCcw, Trash2, AlertTriangle, Clock, Timer,
} from 'lucide-react';

export function FTPBackupSection({
  ftpSettings,
  setFtpSettings,
  ftpTesting,
  ftpTestResult,
  ftpSaving,
  ftpBackups,
  remoteBackups,
  remoteError,
  creatingDump,
  scanningRemote,
  restoringFtp,
  restoreLocalConfirm,
  setRestoreLocalConfirm,
  restoreRemoteConfirm,
  setRestoreRemoteConfirm,
  onTestFTP,
  onSaveFTP,
  onCreateDump,
  onScanRemote,
  onRestoreLocal,
  onRestoreRemote,
  onDeleteBackup,
  ftpSchedule,
  setFtpSchedule,
  onSaveSchedule,
  savingSchedule,
}) {
  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Server className="w-4 h-4" />
                Full Database Backup (FTP)
              </CardTitle>
              <CardDescription>
                Full mongodump backups with optional FTP upload. Auto-backup also creates local .gz files.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onScanRemote} disabled={scanningRemote || !ftpSettings.host} data-testid="btn-scan-remote">
                {scanningRemote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4 mr-1" />}
                Remote
              </Button>
              <Button size="sm" onClick={onCreateDump} disabled={creatingDump} data-testid="btn-create-dump">
                {creatingDump ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Backing up...</> : <><Database className="w-4 h-4 mr-2" /> Backup Now</>}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* FTP Settings */}
          <div className="p-4 border rounded-lg space-y-4">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Settings className="w-3.5 h-3.5" /> FTP Connection
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Host</Label>
                <Input value={ftpSettings.host} onChange={e => setFtpSettings(s => ({ ...s, host: e.target.value }))} placeholder="ftp.example.com" data-testid="ftp-host" />
              </div>
              <div>
                <Label className="text-xs">Port</Label>
                <Input type="number" value={ftpSettings.port} onChange={e => setFtpSettings(s => ({ ...s, port: parseInt(e.target.value) || 21 }))} data-testid="ftp-port" />
              </div>
              <div>
                <Label className="text-xs">Username</Label>
                <Input value={ftpSettings.username} onChange={e => setFtpSettings(s => ({ ...s, username: e.target.value }))} placeholder="ftp_user" data-testid="ftp-username" />
              </div>
              <div>
                <Label className="text-xs">Password</Label>
                <Input type="password" value={ftpSettings.password} onChange={e => setFtpSettings(s => ({ ...s, password: e.target.value }))} placeholder="••••••••" data-testid="ftp-password" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <Label className="text-xs">Remote Path</Label>
                <Input value={ftpSettings.remote_path} onChange={e => setFtpSettings(s => ({ ...s, remote_path: e.target.value }))} placeholder="/backups" data-testid="ftp-remote-path" />
              </div>
              <div className="flex items-center gap-4 pb-1">
                <div className="flex items-center gap-2">
                  <Switch checked={ftpSettings.use_sftp} onCheckedChange={v => setFtpSettings(s => ({ ...s, use_sftp: v, use_tls: v ? false : s.use_tls, port: v ? 22 : 21 }))} data-testid="ftp-use-sftp" />
                  <Label className="text-xs">SFTP</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={ftpSettings.use_tls} onCheckedChange={v => setFtpSettings(s => ({ ...s, use_tls: v, use_sftp: v ? false : s.use_sftp }))} disabled={ftpSettings.use_sftp} data-testid="ftp-use-tls" />
                  <Label className="text-xs">FTPS/TLS</Label>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={onTestFTP} disabled={ftpTesting || !ftpSettings.host} className="flex-1" data-testid="btn-test-ftp">
                  {ftpTesting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wifi className="w-4 h-4 mr-1" />}
                  Test
                </Button>
                <Button size="sm" onClick={onSaveFTP} disabled={ftpSaving || !ftpSettings.host} className="flex-1" data-testid="btn-save-ftp">
                  {ftpSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Save
                </Button>
              </div>
            </div>
            {ftpTestResult && (
              <div className={`flex items-center gap-2 p-2 rounded text-xs ${ftpTestResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`} data-testid="ftp-test-result">
                {ftpTestResult.success ? <CheckCircle className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
                {ftpTestResult.message}
              </div>
            )}
          </div>

          {/* Scheduled FTP Backups */}
          <div className="p-4 border rounded-lg space-y-4">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" /> Scheduled Remote Backups
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div className="flex items-center gap-2 pb-1">
                <Switch
                  checked={ftpSchedule.ftp_schedule_enabled}
                  onCheckedChange={v => setFtpSchedule(s => ({ ...s, ftp_schedule_enabled: v }))}
                  data-testid="ftp-schedule-enabled"
                />
                <Label className="text-xs">Enable scheduled backups</Label>
              </div>
              <div>
                <Label className="text-xs">Interval (hours)</Label>
                <Input
                  type="number"
                  min={1}
                  max={168}
                  value={ftpSchedule.ftp_schedule_interval_hours}
                  onChange={e => setFtpSchedule(s => ({ ...s, ftp_schedule_interval_hours: parseInt(e.target.value) || 12 }))}
                  disabled={!ftpSchedule.ftp_schedule_enabled}
                  data-testid="ftp-schedule-interval"
                />
              </div>
              <div>
                <Label className="text-xs">Max remote backups</Label>
                <Input
                  type="number"
                  min={1}
                  max={10000}
                  value={ftpSchedule.ftp_max_remote_backups}
                  onChange={e => setFtpSchedule(s => ({ ...s, ftp_max_remote_backups: parseInt(e.target.value) || 2000 }))}
                  disabled={!ftpSchedule.ftp_schedule_enabled}
                  data-testid="ftp-schedule-max-backups"
                />
              </div>
              <Button size="sm" onClick={onSaveSchedule} disabled={savingSchedule} data-testid="btn-save-ftp-schedule">
                {savingSchedule ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Save Schedule
              </Button>
            </div>
            {ftpSchedule.ftp_schedule_enabled && (
              <div className="flex items-center gap-2 p-2 rounded text-xs bg-sky-50 text-sky-700 border border-sky-200" data-testid="ftp-schedule-status">
                <Timer className="w-4 h-4 flex-shrink-0" />
                Backups will run automatically every <strong>{ftpSchedule.ftp_schedule_interval_hours} hours</strong>.
                Oldest backups will be rotated after <strong>{ftpSchedule.ftp_max_remote_backups}</strong> remote copies.
              </div>
            )}
          </div>

          {/* Local Backups */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <HardDrive className="w-3.5 h-3.5" /> Local Backups
            </h4>
            {ftpBackups.filter(b => b.status === 'completed').length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No local backups yet. Click "Backup Now" to create one.</p>
            ) : (
              <div className="space-y-2">
                {ftpBackups.filter(b => b.status === 'completed').map(backup => (
                  <div key={backup.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`local-backup-${backup.id}`}>
                    <div className="flex items-center gap-3">
                      <Database className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{backup.filename}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{backup.created_at ? new Date(backup.created_at).toLocaleString() : ''}</span>
                          <span>{backup.size_bytes ? `${(backup.size_bytes / 1024 / 1024).toFixed(1)} MB` : ''}</span>
                          {backup.uploaded_to_ftp && <Badge variant="outline" className="text-[10px] py-0 px-1 border-emerald-300 text-emerald-700">FTP</Badge>}
                          {backup.is_scheduled && <Badge variant="outline" className="text-[10px] py-0 px-1 border-sky-300 text-sky-700">Scheduled</Badge>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => setRestoreLocalConfirm(backup)} disabled={!!restoringFtp} data-testid={`btn-restore-local-${backup.id}`}>
                        {restoringFtp === backup.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => onDeleteBackup(backup.id)} data-testid={`btn-delete-local-${backup.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Remote Backups */}
          {remoteError && (
            <div className="flex items-center gap-2 p-3 rounded-lg text-xs bg-rose-50 text-rose-700 border border-rose-200" data-testid="remote-scan-error">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{remoteError}</span>
            </div>
          )}
          {remoteBackups.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Globe className="w-3.5 h-3.5" /> Remote Backups (FTP)
                <Badge variant="secondary" className="text-xs">{remoteBackups.length}</Badge>
              </h4>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {remoteBackups.map((file, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`remote-backup-${i}`}>
                    <div className="flex items-center gap-3">
                      <FolderOpen className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{file.filename}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{file.year}/{file.month}</span>
                          <span>{file.path}</span>
                          {file.size_bytes && <span>{(file.size_bytes / 1024 / 1024).toFixed(1)} MB</span>}
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setRestoreRemoteConfirm(file)} disabled={!!restoringFtp} data-testid={`btn-restore-remote-${i}`}>
                      {restoringFtp === file.path ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                      Restore
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Local FTP Restore Confirm */}
      <AlertDialog open={!!restoreLocalConfirm} onOpenChange={() => setRestoreLocalConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" /> Restore from Local Backup?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will <strong>drop all current data</strong> and restore from: <br />
              <code className="bg-muted p-1 rounded text-xs">{restoreLocalConfirm?.filename}</code>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onRestoreLocal(restoreLocalConfirm)} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-confirm-local-ftp-restore">
              <RotateCcw className="w-4 h-4 mr-2" /> Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remote FTP Restore Confirm */}
      <AlertDialog open={!!restoreRemoteConfirm} onOpenChange={() => setRestoreRemoteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" /> Restore from Remote Backup?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will download from FTP and <strong>replace all current data</strong>: <br />
              <code className="bg-muted p-1 rounded text-xs">{restoreRemoteConfirm?.path}</code>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onRestoreRemote(restoreRemoteConfirm)} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-confirm-remote-ftp-restore">
              <Globe className="w-4 h-4 mr-2" /> Download & Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
